/**
 * Issue Scanner Documentation
 *
 * Comprehensive documentation for all project issue scanner types.
 * Each entry includes:
 * - title: Display name of the issue
 * - description: Detailed explanation of what the issue detects
 * - formula: The exact calculation/check logic used
 * - examples: Concrete scenarios showing when this issue triggers
 * - severity: 'critical' | 'warning' | 'info'
 */

export const ISSUE_DOCUMENTATION = {
    // =====================================================================
    // ACTIVE PROJECTS - CRITICAL ISSUES
    // =====================================================================

    VENDOR_BILLS_OVER_REVENUE: {
        title: "Vendor bills over Vendor revenue",
        description: "Detects active projects where actual vendor bills exceed the vendor revenue allocated to the project. This indicates the project is losing money on vendor costs because you're paying vendors more than you're billing the client for vendor work. This is a critical financial issue requiring immediate attention.",
        formula: "Received Vendor Bills > Vendor Revenue",
        examples: [
            "Project has received vendor bills totaling $15,000, but only $12,000 was allocated as vendor revenue from client invoices. The project is losing $3,000 on vendor costs.",
            "A marketing campaign project billed vendors $8,500 but the revenue allocation for vendor work is only $7,000. The $1,500 shortfall must be covered by agency margin or internal costs."
        ],
        severity: "critical"
    },

    VENDOR_BILLS_OVER_PLANNED: {
        title: "Vendor Bills Over Planned Cost",
        description: "Detects active projects where actual vendor bills received exceed the originally planned vendor cost budget. This means the project is over budget on vendor expenses and may impact profitability. It could indicate scope creep, poor estimation, or unplanned vendor expenses.",
        formula: "Received Vendor Bills > Vendor Planned Cost",
        examples: [
            "Project was planned with $20,000 vendor budget, but actual vendor bills have reached $23,500. The project is $3,500 over budget.",
            "A web development project estimated $5,000 for hosting/services, but actual bills total $6,800 due to additional server requirements. The budget overrun needs to be addressed."
        ],
        severity: "critical"
    },

    PROJECTS_PAST_END_DATE: {
        title: "Project past end date",
        description: "Detects projects with an end date in the past but status is still 'In-Planning' or 'On-Going' (not 'Finished' or 'Archived'). These projects should be reviewed to either extend the end date or update the status to 'Finished'. Overdue projects can indicate scope changes, delays, or simply forgotten status updates.",
        formula: "End Date < Today AND Status ∈ ['In-Planning', 'On-Going']",
        examples: [
            "Project end date was December 31, 2024, but today is January 15, 2025 and status is still 'On-Going'. The project is 15 days overdue and needs status update or deadline extension.",
            "A campaign ended 45 days ago based on the end date, but project manager forgot to mark it as 'Finished'. Client invoicing and final reporting are likely delayed."
        ],
        severity: "critical"
    },

    REVENUE_NOT_ALLOCATED: {
        title: "Revenue not allocated",
        description: "Detects active projects that have estimated vendor costs but zero vendor revenue allocated. This means client invoices exist but the revenue hasn't been properly split between agency work and vendor work (passthrough). Without proper allocation, project profitability calculations will be inaccurate and vendor costs may appear as losses.",
        formula: "Estimated Vendor Cost > 0 AND Vendor Revenue = $0",
        examples: [
            "Project has $15,000 estimated vendor cost and total client invoice of $50,000, but revenue allocation shows $0 for vendor revenue. The $50,000 needs to be split (e.g., $15,000 vendor + $35,000 agency).",
            "A project with $8,000 in planned vendor expenses shows $0 vendor revenue. The finance team needs to access the invoice allocation matrix to properly allocate the client billing between agency and vendor portions."
        ],
        severity: "critical"
    },

    // =====================================================================
    // ACTIVE PROJECTS - WARNINGS
    // =====================================================================

    VENDOR_REVENUE_LOW: {
        title: "Vendor Revenue Lower Than Planned Cost",
        description: "Detects active projects where the vendor revenue allocated is less than the planned vendor cost. This indicates a potential issue with revenue allocation - either the client billing is too low for the planned vendor work, or the revenue split between agency and vendor needs adjustment. The project may not generate enough revenue to cover vendor costs.",
        formula: "Vendor Revenue < Vendor Planned Cost",
        examples: [
            "Project has $10,000 planned vendor cost but only $8,500 allocated as vendor revenue. There's a $1,500 gap that may impact profitability.",
            "A social media campaign planned $3,000 in influencer costs (vendor) but client invoices only allocated $2,200 as vendor revenue. Need to verify if client billing was sufficient or if allocation needs correction."
        ],
        severity: "warning"
    },

    VENDOR_REVENUE_HIGH: {
        title: "Vendor Revenue Higher Than Planned Cost",
        description: "Detects active projects where vendor revenue exceeds the planned vendor cost. While not necessarily a problem, this could indicate: (1) revenue allocation needs review - too much was marked as vendor passthrough instead of agency work, (2) vendor costs were underestimated, or (3) legitimate scenario where markup on vendor costs is included. This should be reviewed to ensure accurate profit calculations.",
        formula: "Vendor Revenue > Vendor Planned Cost",
        examples: [
            "Project planned $12,000 vendor cost but allocated $15,000 as vendor revenue. The extra $3,000 might be markup on vendor costs, or revenue allocation may need adjustment.",
            "A project shows $5,000 planned vendor expenses but $7,500 vendor revenue. Check if this includes legitimate markup or if some agency work was incorrectly classified as vendor revenue."
        ],
        severity: "warning"
    },

    VENDOR_REVENUE_PLANNED_COST_DISCREPANCY: {
        title: "Discrepancy between Vendor revenue and vendor planned cost",
        description: "Detects active projects where vendor revenue does not match vendor planned cost. It combines the previous lower-than-planned and higher-than-planned checks into one review list.",
        formula: "Vendor Revenue != Vendor Planned Cost",
        examples: [
            "Project has S$10,000 planned vendor cost but only S$8,500 vendor revenue, leaving a S$1,500 gap.",
            "Project has S$12,000 planned vendor cost but S$15,000 vendor revenue, so the allocation should be reviewed."
        ],
        severity: "warning"
    },

    INVOICE_ALLOCATION_VARIANCE: {
        title: "Invoice Allocation Variance",
        description: "Detects discrepancies between two levels of invoice allocation: (1) the high-level sales order invoice allocation amount assigned to the project, and (2) the sum of individual Xero line item allocations for that project. When these don't match, it indicates accounting inconsistencies that can lead to inaccurate project financials and reporting errors. The finance team needs to reconcile the allocation matrix.",
        formula: "Sales Order Invoice Allocation ≠ Sum(Xero Line Item Allocations)",
        examples: [
            "Invoice #12345 shows $10,000 allocated to Project A at the sales order level, but when you add up all the individual line items allocated to Project A, they only total $9,500. There's a $500 variance that needs reconciliation.",
            "Project has $25,000 in sales_order_invoice.amount records, but the detailed Xero line items allocated to the project sum to $26,500. The $1,500 difference must be corrected in the invoice allocation matrix to ensure accurate financial reporting."
        ],
        severity: "warning"
    },

    VENDOR_PLANNING_EMPTY: {
        title: "Vendor planning empty",
        description: "Detects active projects that have an estimated vendor cost entered but no actual vendor planning records (quotations, planned costs). This means the PM estimated vendor expenses but hasn't created the detailed vendor planning entries needed for budget tracking and vendor management. Without vendor planning, you can't track actual vs. planned vendor costs.",
        formula: "Estimated Vendor Cost > 0 AND Vendor Planned Cost = $0",
        examples: [
            "Project shows $8,000 estimated vendor cost in project settings, but the Vendor Planning tab has no entries ($0 total planned). PM needs to add vendor quotations and planning records.",
            "A video production project has $15,000 estimated for vendor costs (equipment rental, talent), but no vendor planning records exist. Create planning entries to track budgets and actual spending."
        ],
        severity: "warning"
    },

    DATE_PRE_START_HOURS: {
        title: "Pre start hours logged",
        description: "Detects 'In-Planning' projects or future 'On-Going' projects (start date hasn't arrived yet) where team members have already logged 10 or more hours. This could indicate: (1) project start date is incorrect and should be earlier, (2) hours are logged to the wrong project, or (3) pre-project work happened that should be tracked differently. Hours logged before the official start may not be billed correctly.",
        formula: "Status ∈ ['In-Planning', 'On-Going (Future)'] AND Total Hours ≥ 10",
        examples: [
            "Project start date is February 1, 2025 (future), but team has already logged 15 hours. Either the start date should be adjusted to today, or hours may be logged to the wrong project.",
            "In-Planning project has 25 hours logged for initial research and planning. If this is legitimate pre-project work, consider adjusting the start date to when work actually began."
        ],
        severity: "warning"
    },

    // =====================================================================
    // FINISHED PROJECTS
    // =====================================================================

    PROJECTS_TO_ARCHIVE: {
        title: "Projects to Archive",
        description: "Detects all projects with 'Finished' status that are ready to be archived. Archiving finished projects helps keep the active projects list clean and focused. Projects in this list have completed all deliverables and billing, and are ready for final archival. It's a reminder to perform the final archival step.",
        formula: "Status = 'Finished'",
        examples: [
            "Project was marked as 'Finished' 45 days ago and all invoices are paid. Ready to archive for historical record-keeping.",
            "A campaign finished 2 weeks ago, all final reports delivered, and client invoicing complete. Can be safely archived."
        ],
        severity: "info"
    },

    // =====================================================================
    // COMPLETED PROJECTS (FINISHED/ARCHIVED)
    // =====================================================================

    VENDOR_LOSS_FINISHED: {
        title: "Finished/Archived Projects with Vendor Loss",
        description: "Detects finished or archived projects where actual vendor bills exceeded the vendor revenue by at least $100. This indicates a vendor cost overrun that wasn't covered by client billing - the agency absorbed the loss. Useful for post-project analysis to understand what went wrong and prevent similar issues in future projects.",
        formula: "Vendor Bills - Vendor Revenue ≥ $100 AND Status ∈ ['Finished', 'Archived']",
        examples: [
            "Finished project received $12,000 in vendor bills but only had $10,500 vendor revenue allocated. The agency absorbed a $1,500 vendor loss that should be analyzed.",
            "An archived campaign shows vendor bills of $8,300 vs. vendor revenue of $7,000. The $1,300 loss may indicate scope creep or billing issues that can inform future project planning."
        ],
        severity: "warning"
    },

    UNPROFITABLE_FINISHED: {
        title: "Unprofitable Finished/Archived Projects",
        description: "Detects finished or archived projects with negative profit (invoiced revenue less than total costs including manhours and vendor bills). These are projects where the agency lost money overall. This analysis helps identify patterns in unprofitable work - which clients, project types, or services tend to lose money, informing future pricing and scoping decisions.",
        formula: "Profit = Invoiced Revenue - (Manhour Cost + Vendor Bills) < $0",
        examples: [
            "Finished project had $45,000 in client invoices but costs were $32,000 manhours + $18,000 vendor bills = $50,000 total. Net loss of $5,000.",
            "Archived project billed client $20,000 but spent $15,000 on internal team time and $8,000 on vendors for $23,000 total cost. Lost $3,000 on the project - review for lessons learned."
        ],
        severity: "warning"
    },

    // =====================================================================
    // ARCHIVED PROJECTS
    // =====================================================================

    HIGHLY_PROFITABLE_ARCHIVED: {
        title: "Highly Profitable Archived Projects",
        description: "Detects archived projects with a profit margin of 10% or higher (some agencies use higher thresholds like 15-20%). These are the agency's most successful projects financially. This analysis helps identify which types of projects, clients, or services are most profitable, informing future business development and service offerings. It's a positive indicator for case studies and best practices.",
        formula: "Profit Margin = (Profit / Revenue) × 100 ≥ 10%",
        examples: [
            "Archived project had $100,000 revenue and $85,000 total costs = $15,000 profit = 15% margin. Excellent financial performance worth analyzing for replication.",
            "A consulting project billed $50,000 with only $40,000 in costs (mostly manhours, minimal vendor costs). 20% margin indicates efficient delivery and good pricing - study this project type for future growth."
        ],
        severity: "info"
    },

    // =====================================================================
    // ALL PROJECTS
    // =====================================================================

    NO_CLIENT_INVOICES: {
        title: "No Client invoice",
        description: "Detects non-archived confirmed projects that have no client invoices linked. This could indicate: (1) project is pre-billing phase, (2) invoices exist in Xero but haven't been synced/linked to the project, (3) billing was forgotten or delayed, or (4) project is internal/pro-bono. For billable projects, missing invoices mean revenue isn't being tracked and cash flow may be impacted.",
        formula: "Project Type = 'CONFIRMED' AND Status != 'Archived' AND Invoice Count = 0",
        examples: [
            "On-going project that started 3 months ago has no client invoices. Either billing hasn't started, or invoices need to be linked to the project in the system.",
            "Finished project shows no invoices - either this was pro-bono work that should be marked differently, or invoicing was overlooked and needs immediate attention."
        ],
        severity: "warning"
    },

    NO_SALES_ORDERS: {
        title: "No SO",
        description: "Detects non-archived confirmed projects that have no sales order linked. This usually means the sales document step has not been completed yet, or the sales order exists but has not been connected to the project.",
        formula: "Project Type = 'CONFIRMED' AND Status != 'Archived' AND Sales Order Count = 0",
        examples: [
            "An on-going confirmed project has no sales order connected, so billing setup should be reviewed.",
            "A finished project has no SO, which may mean the SO was created in Xero but not linked back to the project."
        ],
        severity: "warning"
    },

    INVOICES_AFTER_END_DATE: {
        title: "Projects with Invoices Outside Date Range",
        description: "Detects projects that have client invoices dated outside the project's start and end date range (either before start date or after end date). This could indicate: (1) project dates need adjustment, (2) invoices were issued for work outside the project timeline, (3) pre-project or post-project billing, or (4) data entry errors in invoice or project dates. This should be reviewed to ensure accurate project timeline tracking and proper period-based revenue recognition.",
        formula: "Invoice Date < Project Start Date OR Invoice Date > Project End Date",
        examples: [
            "Project starts January 1, 2025, but has an invoice dated December 15, 2024. Either the project start date should be moved earlier or the invoice needs correction.",
            "Project ended on December 31, 2024, but has an invoice dated January 15, 2025. Either the project end date should be extended or the invoice date needs correction.",
            "Project runs from March 1 to March 31, but has invoices dated February 28 and April 5. Both invoices fall outside the project timeline and should be reviewed."
        ],
        severity: "warning"
    },

    BILLS_AFTER_END_DATE: {
        title: "Projects with Vendor Bills Outside Date Range",
        description: "Detects projects that have vendor bills dated outside the project's start and end date range (either before start date or after end date). This could indicate: (1) project dates need adjustment, (2) vendors invoiced for work outside the project timeline, (3) pre-project or post-project vendor work, or (4) data entry errors in bill or project dates. Bills outside the project timeline can impact accurate profitability calculations and budget tracking.",
        formula: "Vendor Bill Date < Project Start Date OR Vendor Bill Date > Project End Date",
        examples: [
            "Project starts February 1, but has a vendor bill dated January 25 for setup work. The project start date may need to be moved earlier to include this cost.",
            "Project ended on October 31, but received a vendor bill dated November 10. The project end date may need extension or the bill date requires verification.",
            "Project runs from June 1 to June 30, but has vendor bills dated May 28 and July 5. Both bills fall outside the project dates and should be reviewed for proper allocation."
        ],
        severity: "warning"
    },

    TIME_OUTSIDE_DATE_RANGE: {
        title: "Projects with Time Entries Outside Date Range",
        description: "Detects projects that have time entries (work accomplished dates) logged outside the project's start and end date range (either before start date or after end date). This could indicate: (1) project dates need adjustment to include pre-work or wrap-up time, (2) team members logged time to wrong project, (3) timesheet entries with incorrect dates, or (4) legitimate pre-project planning or post-project finalization work. Time entries outside the project timeline can lead to inaccurate project costing and resource tracking.",
        formula: "Time Entry Date < Project Start Date OR Time Entry Date > Project End Date",
        examples: [
            "Project starts March 1, but team members logged 15 hours on February 25-28 for planning and setup. The project start date should be adjusted or these hours should be moved to a different project/task.",
            "Project ended on May 31, but developers logged 8 hours on June 5 for bug fixes. Either extend the project end date or verify if this work belongs to a maintenance project.",
            "Project runs from July 1 to July 31, but has time entries on June 29 (pre-work) and August 3 (wrap-up). Total 23 hours outside project dates affecting cost calculations."
        ],
        severity: "warning"
    },

    WORK_ACCOMPLISHED_OUTSIDE_RANGE: {
        title: "Invoice Work Accomplished Date Outside Project Range",
        description: "Detects projects where invoice revenue recognition dates (work accomplished date / allocated_at) fall outside the project's start and end date range. The work accomplished date indicates when revenue from an invoice should be recognized, and is used for accurate financial reporting and revenue recognition timing. When these dates fall outside the project timeline, it can cause: (1) revenue recognition in wrong accounting periods, (2) mismatched project timelines and financial reporting, (3) inaccurate project profitability calculations for specific periods, or (4) audit issues with revenue recognition timing.",
        formula: "Invoice Allocation Work Accomplished Date < Project Start Date OR Work Accomplished Date > Project End Date",
        examples: [
            "Project runs from February 1 to March 31, 2025, but an invoice has work accomplished date of January 28, 2025. Revenue is being recognized before the project officially started.",
            "Project ended on December 31, 2024, but an invoice allocation has work accomplished date of January 15, 2025. This causes Q1 2025 revenue to be attributed to a project that ended in Q4 2024.",
            "A 6-month project from July to December has an invoice with work accomplished date in the following March. The revenue recognition is delayed beyond the project completion, affecting period-end financial reports."
        ],
        severity: "warning"
    }
};

/**
 * Get documentation for a specific issue type
 * @param {string} issueCode - The issue code (e.g., 'VENDOR_BILLS_OVER_REVENUE')
 * @returns {Object|null} Documentation object or null if not found
 */
export const getIssueDocumentation = (issueCode) => {
    return ISSUE_DOCUMENTATION[issueCode] || null;
};

/**
 * Get all issue codes
 * @returns {string[]} Array of all issue codes
 */
export const getAllIssueCodes = () => {
    return Object.keys(ISSUE_DOCUMENTATION);
};

/**
 * Get issues by severity
 * @param {string} severity - 'critical' | 'warning' | 'info'
 * @returns {Object[]} Array of issue documentation objects matching the severity
 */
export const getIssuesBySeverity = (severity) => {
    return Object.entries(ISSUE_DOCUMENTATION)
        .filter(([_, doc]) => doc.severity === severity)
        .map(([code, doc]) => ({ code, ...doc }));
};
